import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../supabaseClient';

const UserManager = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('users')
        .select(`
            *,
            spam_reports:spam_reports!reported_id(count),
            friendships_1:friendships!user_id_1(count),
            friendships_2:friendships!user_id_2(count)
        `)
        .order('created_at', { ascending: false });
      
      if (searchTerm) {
        query = query.or(`username.ilike.%${searchTerm}%,display_name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%`);
      }

      const { data, error } = await query;
      if (error) throw error;
      
      // Process data to flatten counts
      const processedData = data.map(user => ({
          ...user,
          spamCount: user.spam_reports?.[0]?.count || 0,
          friendCount: (user.friendships_1?.[0]?.count || 0) + (user.friendships_2?.[0]?.count || 0)
      }));

      setUsers(processedData || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchUsers();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [fetchUsers]);

  const handleDeleteClick = (user) => {
      setUserToDelete(user);
      setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
      if (!userToDelete) return;
      setLoading(true);
      try {
          // 1. Delete friendships
          await supabase.from('friendships').delete().or(`user_id_1.eq.${userToDelete.id},user_id_2.eq.${userToDelete.id}`);
          
          // 2. Delete blocked_users
          await supabase.from('blocked_users').delete().or(`blocker_id.eq.${userToDelete.id},blocked_id.eq.${userToDelete.id}`);

          // 3. Delete spam_reports
          await supabase.from('spam_reports').delete().or(`reporter_id.eq.${userToDelete.id},reported_id.eq.${userToDelete.id}`);

          // 4. Unlink expenses (payer_user_id) - Optional: set to null to keep record but unlink
          await supabase.from('expenses').update({ payer_user_id: null }).eq('payer_user_id', userToDelete.id);

          // 5. Delete user
          const { error } = await supabase.from('users').delete().eq('id', userToDelete.id);
          if (error) throw error;

          alert('ลบผู้ใช้เรียบร้อยแล้ว');
          fetchUsers();
      } catch (error) {
          console.error('Error deleting user:', error);
          alert('ลบไม่สำเร็จ: ' + error.message);
      } finally {
          setLoading(false);
          setIsDeleteModalOpen(false);
          setUserToDelete(null);
      }
  };

  return (
    <div style={{ padding: '20px', backgroundColor: 'var(--color-bg)', minHeight: '100vh', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
      <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ color: 'var(--color-primary)', margin: 0, fontWeight: '700' }}>👥 User Manager ({users.length})</h2>
          <button 
            onClick={fetchUsers} 
            disabled={loading}
            style={{ padding: '8px 16px', backgroundColor: 'var(--color-primary-soft)', color: 'var(--color-primary)', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '5px' }}
          >
            🔄 Refresh
          </button>
        </div>
        
        <div style={{ marginBottom: '20px' }}>
          <input
            type="text"
            placeholder="🔍 Search by Name, Username, Email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '16px',
              borderRadius: '12px',
              border: '1px solid var(--color-border)',
              fontSize: '16px',
              boxSizing: 'border-box',
              boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
              outlineColor: 'var(--color-primary)'
            }}
          />
        </div>

        {loading ? <p style={{ textAlign: 'center', color: 'var(--color-text-muted)' }}>Loading users...</p> : (
          <div style={{ backgroundColor: '#fff', borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', overflow: 'hidden', border: '1px solid var(--color-border)' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
                <thead style={{ backgroundColor: 'var(--color-primary-soft)', borderBottom: '2px solid var(--color-primary-soft)' }}>
                  <tr>
                    <th style={{ padding: '20px', textAlign: 'left', color: 'var(--color-primary)', fontSize: '14px', fontWeight: '600' }}>User Profile</th>
                    <th style={{ padding: '20px', textAlign: 'left', color: 'var(--color-primary)', fontSize: '14px', fontWeight: '600' }}>Contact Info</th>
                    <th style={{ padding: '20px', textAlign: 'center', color: 'var(--color-primary)', fontSize: '14px', fontWeight: '600' }}>Stats</th>
                    <th style={{ padding: '20px', textAlign: 'left', color: 'var(--color-primary)', fontSize: '14px', fontWeight: '600' }}>Joined Date</th>
                    <th style={{ padding: '20px', textAlign: 'right', color: 'var(--color-primary)', fontSize: '14px', fontWeight: '600' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => (
                    <tr key={user.id} style={{ borderBottom: '1px solid var(--color-surface-muted)', transition: 'background-color 0.2s' }}>
                      <td style={{ padding: '20px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                          {user.picture_url ? (
                            <img src={user.picture_url} alt="" style={{ width: '48px', height: '48px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #fff', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }} />
                          ) : (
                            <div style={{ width: '48px', height: '48px', borderRadius: '50%', backgroundColor: 'var(--color-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>👤</div>
                          )}
                          <div>
                            <div style={{ fontWeight: 'bold', color: 'var(--color-text)', fontSize: '16px' }}>{user.display_name}</div>
                            <div style={{ fontSize: '13px', color: 'var(--color-text-subtle)' }}>@{user.username || 'no-username'}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '20px' }}>
                        <div style={{ fontSize: '14px', color: 'var(--color-text-muted)', marginBottom: '4px' }}>📧 {user.email || '-'}</div>
                        <div style={{ fontSize: '14px', color: 'var(--color-text-muted)' }}>📞 {user.phone_number || '-'}</div>
                      </td>
                      <td style={{ padding: '20px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', alignItems: 'center' }}>
                            <span style={{ fontSize: '12px', color: 'var(--color-text-muted)', backgroundColor: 'var(--color-surface-muted)', padding: '4px 8px', borderRadius: '12px', border: '1px solid var(--color-border)' }}>
                                👥 {user.friendCount} เพื่อน
                            </span>
                            {user.spamCount > 0 && (
                                <span style={{ fontSize: '12px', color: '#c62828', backgroundColor: '#ffebee', padding: '4px 8px', borderRadius: '12px', border: '1px solid #ffcdd2', fontWeight: 'bold' }}>
                                    🚩 {user.spamCount} Spam
                                </span>
                            )}
                        </div>
                      </td>
                      <td style={{ padding: '20px', color: 'var(--color-text-muted)', fontSize: '14px' }}>
                        {new Date(user.created_at).toLocaleDateString('th-TH', { year: 'numeric', month: 'long', day: 'numeric' })}
                      </td>
                      <td style={{ padding: '20px', textAlign: 'right' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '5px' }}>
                            <span style={{ color: 'var(--color-text-subtle)', fontSize: '12px', fontFamily: 'monospace' }}>#{user.id}</span>
                            <button onClick={() => handleDeleteClick(user)} style={{ padding: '6px 12px', backgroundColor: '#fff', border: '1px solid #ffcdd2', color: '#d32f2f', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}>ลบผู้ใช้</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {users.length === 0 && <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-text-subtle)' }}>No users found matching your search.</div>}
          </div>
        )}

        {isDeleteModalOpen && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 2200 }}>
            <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '16px', width: '90%', maxWidth: '450px', boxShadow: '0 20px 40px rgba(0,0,0,0.1)', textAlign: 'center' }}>
              <h3 style={{ margin: '0 0 15px 0', color: 'var(--color-text)' }}>ยืนยันการลบผู้ใช้</h3>
              <p style={{ color: 'var(--color-text-muted)', marginBottom: '15px' }}>คุณต้องการลบผู้ใช้ <strong>"{userToDelete?.display_name}"</strong> ออกจากระบบถาวรใช่หรือไม่?</p>
              <p style={{ color: '#d32f2f', fontSize: '13px', marginBottom: '25px', backgroundColor: '#ffebee', padding: '10px', borderRadius: '8px' }}>⚠️ การกระทำนี้จะลบข้อมูลความสัมพันธ์, การบล็อก, และรายงานสแปมทั้งหมดที่เกี่ยวข้องด้วย</p>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '15px' }}>
                <button 
                  onClick={() => setIsDeleteModalOpen(false)}
                  style={{ padding: '10px 20px', backgroundColor: '#fff', color: 'var(--color-text-muted)', border: '1px solid var(--color-border)', borderRadius: '8px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer' }}
                >
                  ยกเลิก
                </button>
                <button 
                  onClick={confirmDelete}
                  style={{ padding: '10px 20px', backgroundColor: '#f44336', color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 4px 6px rgba(244, 67, 54, 0.3)' }}
                >
                  {loading ? 'กำลังลบ...' : 'ยืนยันลบ'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManager;
